if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type'] = 'application/json'; // Changed to JSON

        // Get the main product form data
        const formData = new FormData(this.form);

        // Create items array with main product
        const items = [
          {
            id: formData.get('id'),
            quantity: parseInt(formData.get('quantity'), 10) || 1,
          },
        ];

        // Check for upsell item
        const upsellCheckbox = document.querySelector('.upsell-checkbox');
        const upsellVariantSelect = document.querySelector('.upsell-size-option');
        const upsellQuantityInput = document.querySelector('.upsell-quantity-selector__input');

        // Add upsell item if selected
        if (upsellCheckbox && upsellCheckbox.checked) {
          items.push({
            id: upsellVariantSelect.value,
            quantity: parseInt(upsellQuantityInput?.value || '1', 10),
          });
        }

        // Pre-add freshen: third-party apps (e.g. Redo) inject line-item
        // properties async after /cart/add. If the existing line has those
        // properties and our new POST doesn't, Shopify creates a duplicate
        // instead of merging. Read current cart and copy any matching
        // variant's properties onto our payload so merges succeed.
        const freshenItems = async () => {
          try {
            const r = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
            if (!r.ok) return;
            const cart = await r.json();
            const byVariant = new Map();
            (cart.items || []).forEach((line) => {
              const k = String(line.variant_id);
              if (!byVariant.has(k)) byVariant.set(k, line.properties || {});
            });
            items.forEach((item) => {
              const existing = byVariant.get(String(item.id));
              if (existing && Object.keys(existing).length > 0) {
                // Deep-merge: existing props provide the match key for merging,
                // form props (if any) win on conflict
                item.properties = { ...existing, ...(item.properties || {}) };
              }
            });
          } catch (e) {
            // If freshen fails, fall through with original payload
          }
        };

        freshenItems().then(() => {
          // Prepare the request body
          const requestBody = {
            items: items,
          };

          // Add sections data if cart exists
          if (this.cart) {
            requestBody.sections = this.cart.getSectionsToRender().map((section) => section.id);
            requestBody.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = JSON.stringify(requestBody);

          return fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }

              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                      this.scheduleCartRefresh();
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(response);
                this.scheduleCartRefresh();
              }
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');
            });
        });
      }

      // After the initial render from the /cart/add response, schedule one
      // delayed re-render of the cart surface. Third-party apps (e.g. Redo)
      // often mutate cart line items asynchronously ~1-2s after the add; this
      // second pass picks up those mutations so the drawer reflects current
      // server truth, preventing stale "duplicate line" appearances.
      scheduleCartRefresh() {
        if (!this.cart) return;
        if (this._cartRefreshTimer) clearTimeout(this._cartRefreshTimer);
        this._cartRefreshTimer = setTimeout(async () => {
          try {
            const sections = this.cart.getSectionsToRender().map((s) => s.id).join(',');
            const r = await fetch(`${routes.cart_url}?sections=${encodeURIComponent(sections)}`, {
              headers: { Accept: 'application/json' },
            });
            if (!r.ok) return;
            const data = await r.json();
            // Emulate the shape renderContents expects
            this.cart.renderContents({ sections: data, id: null });
          } catch (e) {
            // Non-fatal; the initial render is already on screen
          }
        }, 1800);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
